import { memo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCartContext, useAuthContext, useWarehouseContext } from "../../app/providers/contextIndex";
import { Button } from "primereact/button";
import AppToast from "./AppToast";
import LandingMobile from "./LandingMobile";
import { getCompany } from "../../config/companyConfig";
import useMobile from "../../utils/useMobile";
import useTopbarSession from "./topbar/useTopbarSession";
import useTopbarSearch from "./topbar/useTopbarSearch";
import useCartPreview from "./topbar/useCartPreview";
import useWarehouseData from "./topbar/useWarehouseData";
import useTopbarMenu from "./topbar/useTopbarMenu";
import useCheckoutCotizacionToast from "./topbar/useCheckoutCotizacionToast";
import TopbarCartPreview from "./topbar/TopbarCartPreview";
import TopbarSearch from "./topbar/TopbarSearch";
import TopbarWarehouseSelector from "./topbar/TopbarWarehouseSelector";
import TopbarAuthButtons from "./topbar/TopbarAuthButtons";
import TopbarDesktopMegaMenu from "./topbar/TopbarDesktopMegaMenu";
import TopbarMessageBar from "./topbar/TopbarMessageBar";
import { Image } from "primereact/image";

const Topbar = memo(({ config }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckoutFlow = location.pathname.startsWith("/checkout");
  const {
    rotatingMessages = [],
    utilityLinks = [],
    logo = {},
    collections = [],
    searchPlaceholder = "",
    navItems = [],
    catalogMenu = [],
  } = config || {};
  const {
    cartItems,
    cartCount,
    cotizacionId,
    isCartLoading,
    updateCartQuantityFromBackend,
    loadCotizacionFromBackend,
  } = useCartContext();
  const { appUser, isAuthenticated } = useAuthContext();
  const warehouseCtx = useWarehouseContext();

  const isMobileView = useMobile();
  const toastRef = useRef(null);

  const menu = useTopbarMenu({
    collections,
    catalogMenu,
    navItems,
    isMobileView,
    isCheckoutFlow,
    navigate,
  });

  const { warehouseOptions } = useWarehouseData(warehouseCtx);

  useCheckoutCotizacionToast({ cotizacionId, toastRef });

  const { handleLogout } = useTopbarSession({ toastRef });

  const {
    searchTerm,
    setSearchTerm,
    isListening,
    isVoiceSupported,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleVoiceInput,
  } = useTopbarSearch({
    selectedCollection: menu.selectedCollection,
    selectedSubcollection: menu.selectedSubcollection,
    menuStructure: menu.menuStructure,
    selectedAlmacen: warehouseCtx.selectedAlmacen,
    toastRef,
  });

  const {
    previewStatus,
    handlePreviewRemoveItem,
    handlePreviewQuantityChange,
  } = useCartPreview({
    updateCartQuantityFromBackend,
    loadCotizacionFromBackend,
    selectedAlmacen: warehouseCtx.selectedAlmacen,
    toastRef,
  });

  const isCartPage = location.pathname === "/carro";

  return (
    <>
      <TopbarMessageBar rotatingMessages={rotatingMessages} utilityLinks={utilityLinks} />

      <div className={`landing-navbar surface-ground ${menu.isDesktopMenuOpen ? "is-desktop-open" : ""}`}>
        {isMobileView ? (
          <LandingMobile
            logo={logo}
            cartCount={cartCount}
            isCartLoading={isCartLoading}
            shouldShowCollections={menu.shouldShowCollections}
            menuStructure={menu.menuStructure}
            isMobileMenuOpen={menu.isMobileMenuOpen}
            closeMobileMenu={menu.closeMobileMenu}
            setIsMobileMenuOpen={menu.setIsMobileMenuOpen}
            warehouseOptions={warehouseOptions}
            selectedAlmacen={warehouseCtx.selectedAlmacen}
            setSelectedAlmacen={warehouseCtx.setSelectedAlmacen}
            disabled={isCheckoutFlow}
            appUser={appUser}
            handleOpenLogin={menu.handleOpenLogin}
            handleLogout={handleLogout}
            handleAllProductsNavigation={menu.handleAllProductsNavigation}
            handleCatalogNavigation={menu.handleCatalogNavigation}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleSearchSubmit={handleSearchSubmit}
            handleSearchKeyDown={handleSearchKeyDown}
            handleVoiceInput={() => handleVoiceInput(toastRef)}
            isListening={isListening}
            isVoiceSupported={isVoiceSupported}
            searchPlaceholder={searchPlaceholder}
          />
        ) : (
          <div className="landing-container landing-navbar-primary">
            <Link to="/" className="landing-logo-link" aria-label="Ir al inicio">
              <Image src={logo.src} alt={logo.alt} className="landing-logo" />
            </Link>
            {menu.shouldShowDesktopCollections ? (
              <Button
                className={`border-1 surface-border shadow-1 ${menu.isDesktopMenuOpen ? "is-open" : ""}`}
                onClick={menu.handleDesktopMenuToggle}
                aria-label="Abrir categorías"
                aria-expanded={menu.isDesktopMenuOpen}
                icon="pi pi-bars"
                label="Categorías"
                size="medium"
                outlined
                disabled={!menu.menuStructure.length}
              />
            ) : null}
            <TopbarSearch
              searchPlaceholder={searchPlaceholder}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isListening={isListening}
              isVoiceSupported={isVoiceSupported}
              onSearchSubmit={handleSearchSubmit}
              onSearchKeyDown={handleSearchKeyDown}
              onVoiceInput={() => handleVoiceInput(toastRef)}
            />
            <TopbarWarehouseSelector
              warehouseOptions={warehouseOptions}
              selectedAlmacen={warehouseCtx.selectedAlmacen}
              onSelect={warehouseCtx.setSelectedAlmacen}
              disabled={isCheckoutFlow}
            />
            <Button
              className="border-1 surface-border shadow-1"
              icon="pi pi-phone"
              label="Cotizar"
              outlined
              severity="danger"
              style={{ marginLeft: "auto" }}
              onClick={() => window.open(getCompany().whatsappUrl, "_blank")}
            />
            <TopbarAuthButtons
              onOpenLogin={menu.handleOpenLogin}
              onLogout={handleLogout}
            />
            <TopbarCartPreview
              cartItems={cartItems}
              cartCount={cartCount}
              isCartLoading={isCartLoading}
              isAuthenticated={isAuthenticated}
              isCartPage={isCartPage}
              previewStatus={previewStatus}
              onRemoveItem={handlePreviewRemoveItem}
              onQuantityChange={handlePreviewQuantityChange}
            />
          </div>
        )}

        {menu.shouldShowDesktopCollections && (
          <TopbarDesktopMegaMenu
            menuStructure={menu.menuStructure}
            isDesktopMenuOpen={menu.isDesktopMenuOpen}
            activeDesktopCollection={menu.activeDesktopCollection}
            activeDesktopGroup={menu.activeDesktopGroup}
            onCollectionHover={menu.handleDesktopCollectionHover}
            onGroupHover={menu.handleDesktopGroupHover}
            onAllProductsNavigation={menu.handleAllProductsNavigation}
            onCatalogNavigation={menu.handleCatalogNavigation}
            onCancelClose={menu.cancelDesktopMenuClose}
            onScheduleClose={menu.scheduleDesktopMenuClose}
            onSetSelectedCollection={menu.setSelectedCollection}
            onSetSelectedSubcollection={menu.setSelectedSubcollection}
            onSetDesktopMenuOpen={menu.setIsDesktopMenuOpen}
          />
        )}

        <AppToast ref={toastRef} />
      </div>
    </>
  );
});

export default Topbar;